/**
 * CalendarInlineView — 채널 안 캘린더 인라인 뷰
 *
 * 메인 캘린더와 분리된 self-contained 모듈:
 *  - 자체 Context (CalendarContext.Provider) — EventCreateModal/Detail이 의존하는 refreshEvents 제공
 *  - fetchEvents({ channelId }) 로 채널 소유 + 채널 공유 일정만 fetch
 *  - 메인 캘린더의 Month/Week/Day/Agenda View 컴포넌트 재사용
 *  - 카테고리화는 currentChannelId=roomId → channel_mine / channel_shared 구분
 *  - "공유받은 일정 포함" 토글 — off 시 channel_shared 카테고리 숨김
 *  - [+ 새 채널 일정] 버튼 — forceChannel 로 모달 진입
 *  - 게스트도 채널 멤버라면 채널 일정 작성 가능 (visibility=channel + owner_channel_id=roomId)
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
  CalendarEvent, CalendarViewMode, categorizeEvent, eventChipStyle, chipMarker,
  getUserCalendarView, setUserCalendarView, loadCalendarFilters,
} from '../../models/CalendarEvent';
import { CalendarContext, DateRange } from '../../context/CalendarContext';
import { fetchEvents, fetchChannelTaskDeadlines } from '../../services/CalendarService';
import { useCalendarRangeData, useCalendarUserMeta, CalendarRangeData } from '../calendar/useCalendarRangeData';
import TaskPreviewDialog from '../calendar/TaskPreviewDialog';
import { My } from '../../services/My';
import { useAppUIContext } from '../../context/AppUIContext';
import { expandOccurrences } from '../calendar/recurrence';
import { useEventReminders } from '../calendar/useEventReminders';
import { useHolidays } from '../calendar/useHolidays';
import { useCalendarLabels } from '../calendar/useCalendarLabels';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  CalendarDays, PenLine, ChevronLeft, ChevronRight,
  LayoutGrid, Rows2, List, Calendar as DayIcon, Hash, Search, X,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import MonthView from '../calendar/views/MonthView';
import AgendaView from '../calendar/views/AgendaView';
import WeekView from '../calendar/views/WeekView';
import DayView from '../calendar/views/DayView';
import EventCreateModal from '../calendar/EventCreateModal';
import EventDetailOverlay from '../calendar/EventDetailOverlay';

interface Props {
  workspaceId: string;
  roomId: string;
  channelName: string;
}

const VIEW_OPTIONS: { value: CalendarViewMode; icon: React.ElementType }[] = [
  { value: 'month',  icon: LayoutGrid },
  { value: 'week',   icon: Rows2 },
  { value: 'day',    icon: DayIcon },
  { value: 'agenda', icon: List },
];

function formatRangeLabel(mode: CalendarViewMode, anchor: Date): string {
  const loc = i18n.locale || 'en';
  switch (mode) {
    case 'month': return anchor.toLocaleDateString(loc, { year: 'numeric', month: 'long' });
    case 'week': {
      const day = anchor.getDay();
      const start = new Date(anchor);
      start.setDate(start.getDate() - day);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const f = { month: 'short', day: 'numeric' } as const;
      return `${start.toLocaleDateString(loc, f)} – ${end.toLocaleDateString(loc, f)}`;
    }
    case 'day': return anchor.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
    case 'agenda': return anchor.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' });
  }
}

function computeRange(mode: CalendarViewMode, anchor: Date): DateRange {
  const start = new Date(anchor);
  const end = new Date(anchor);
  switch (mode) {
    case 'month': {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - 7);
      end.setMonth(end.getMonth() + 1);
      end.setDate(1);
      end.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() + 14);
      break;
    }
    case 'week': {
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 7);
      break;
    }
    case 'day': {
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 1);
      end.setHours(0, 0, 0, 0);
      break;
    }
    case 'agenda':
    default: {
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 30);
      break;
    }
  }
  return { start, end };
}

function expandRecurringEvents(events: CalendarEvent[], rangeStart: Date, rangeEnd: Date): CalendarEvent[] {
  const result: CalendarEvent[] = [];
  for (const ev of events) {
    if (!ev.recurrence_rule) { result.push(ev); continue; }
    const dtstart = new Date(ev.start_at);
    const durationMs = new Date(ev.end_at).getTime() - dtstart.getTime();
    const occurrences = expandOccurrences({
      rrule: ev.recurrence_rule, dtstart, rangeStart, rangeEnd,
    });
    for (const occ of occurrences) {
      result.push({
        ...ev,
        id: `${ev.id}::${occ.toISOString()}`,
        start_at: occ.toISOString(),
        end_at: new Date(occ.getTime() + durationMs).toISOString(),
      });
    }
  }
  return result;
}

const CHANNEL_SHARED_PREF_KEY = 'calendar-channel-include-shared';
const CHANNEL_TASKS_PREF_KEY = 'calendar-channel-include-tasks';

const CalendarInlineView: React.FC<Props> = ({ workspaceId, roomId, channelName }) => {
  const { rooms } = useAppUIContext();

  const [viewMode, setViewModeState] = useState<CalendarViewMode>(() => getUserCalendarView() ?? 'month');
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const detailAnchorRef = useRef<HTMLElement | null>(null); // 상세 팝오버 앵커 (클릭한 칩)

  // 공유받은 일정 포함 토글 (localStorage 영구)
  const [includeShared, setIncludeShared] = useState<boolean>(() => {
    try { return localStorage.getItem(CHANNEL_SHARED_PREF_KEY) !== '0'; } catch { return true; }
  });
  const toggleIncludeShared = (v: boolean) => {
    setIncludeShared(v);
    try { localStorage.setItem(CHANNEL_SHARED_PREF_KEY, v ? '1' : '0'); } catch {}
  };

  // Tasks 마감일 포함 토글 (localStorage 영구)
  const [includeTasks, setIncludeTasks] = useState<boolean>(() => {
    try { return localStorage.getItem(CHANNEL_TASKS_PREF_KEY) !== '0'; } catch { return true; }
  });
  const toggleIncludeTasks = (v: boolean) => {
    setIncludeTasks(v);
    try { localStorage.setItem(CHANNEL_TASKS_PREF_KEY, v ? '1' : '0'); } catch {}
  };

  // 키워드 검색 (title / description / location 부분일치, 대소문자 무시)
  const [searchQuery, setSearchQuery] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [createInitialDate, setCreateInitialDate] = useState<Date | null>(null);
  const [taskPreview, setTaskPreview] = useState<CalendarEvent | null>(null);

  const range = useMemo(() => computeRange(viewMode, anchorDate), [viewMode, anchorDate]);

  const setViewMode = useCallback((mode: CalendarViewMode) => {
    setViewModeState(mode);
    setUserCalendarView(mode);
  }, []);

  // 범위 데이터 fetcher — SWR/TTL/캐시/늦은응답 가드는 useCalendarRangeData 가 담당
  const fetchRange = useCallback(async (r: DateRange): Promise<CalendarRangeData> => {
    const [list, tasks] = await Promise.all([
      fetchEvents({
        workspaceId,
        rangeStart: r.start.toISOString(),
        rangeEnd: r.end.toISOString(),
        channelId: roomId,
      }),
      fetchChannelTaskDeadlines({
        workspaceId, channelId: roomId,
        rangeStart: r.start.toISOString(),
        rangeEnd: r.end.toISOString(),
      }).catch((e) => { console.warn('[CalendarInlineView] task 마감일 로드 실패:', e); return [] as CalendarEvent[]; }),
    ]);
    return { events: list, taskEvents: tasks };
  }, [workspaceId, roomId]);

  const {
    events, setEvents, taskEvents, loading, setLoading, error, refresh,
  } = useCalendarRangeData({
    scopeKey: `channel|${workspaceId}|${roomId}`,
    range,
    enabled: !!workspaceId && !!roomId,
    fetchRange,
    errorFallback: t`Failed to load events`,
  });

  // 사용자 단위 메타(참석 ID·리마인더 맵) — 채널/메인 캘린더가 모듈 캐시를 공유 (중복 fetch 없음)
  const { attendingEventIds, remindersMap, refreshUserMeta } = useCalendarUserMeta();

  // 일정 생성/수정/삭제 후 강제 갱신 (모달들이 context.refreshEvents 로 호출)
  const refreshEvents = useCallback(async () => {
    await Promise.all([refresh(), refreshUserMeta()]);
  }, [refresh, refreshUserMeta]);

  // 본인이 속한 채널 집합 (categorizeEvent 용)
  const myChannelIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rooms) {
      if (r.workspaceId === workspaceId
        && (r.visibility === 'listed' || r.visibility === 'unlisted')
        && !r.isOneToOne) set.add(r.id);
    }
    return set;
  }, [rooms, workspaceId]);

  // 채널 캘린더 안에선 currentChannelId=roomId → channel_shared vs channel_mine 구분
  const labels = useCalendarLabels();
  const getStyle = useCallback((event: CalendarEvent) => {
    const category = categorizeEvent(
      event, My.user.id, myChannelIds, attendingEventIds.has(event.id), roomId,
    );
    return { ...eventChipStyle(category), marker: chipMarker(category), label: labels.category(category) };
  }, [myChannelIds, attendingEventIds, roomId, labels]);

  // 공휴일은 개인(per-user) 설정 기반 — 메인 캘린더와 동일한 개인 국가 선택을 채널 캘린더에도 적용
  const personalFilters = useMemo(() => loadCalendarFilters(), []);
  const { events: holidayEvents } = useHolidays({
    workspaceId, rangeStart: range.start, rangeEnd: range.end,
    enabled: personalFilters.holidays,
    selectedCountries: personalFilters.holidayCountries,
  });

  const filteredEvents = useMemo(() => {
    const expanded = expandRecurringEvents(events, range.start, range.end);
    const filtered = expanded.filter((e) => {
      // 1) 개인 일정 (owner_channel_id=null) 은 채널 캘린더에 표시 X
      //    - 본인 개인 일정의 채널 공유: 메인 캘린더에 personal 시그널 (휴가/출장 알림용) — 채널 컨텍스트 무관
      //    - 타인 개인 일정의 채널 공유: 동일하게 본인 채널 캘린더에선 노이즈
      if (e.owner_channel_id == null) return false;

      // 2) 이 채널 소유 일정 (owner_channel_id == roomId) → 항상 표시
      if (e.owner_channel_id === roomId) return true;

      // 3) 타 채널이 owner 인 일정 (이 채널에 공유받음) → 토글로 켜고 끔
      return includeShared;
    });
    const tasks = includeTasks ? taskEvents : [];
    const merged = [...filtered, ...holidayEvents, ...tasks];

    // 키워드 부분일치 — title / description / location
    const q = searchQuery.trim().toLowerCase();
    if (!q) return merged;
    return merged.filter((e) => {
      const hay = `${e.title ?? ''} ${e.description ?? ''} ${e.location ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [events, range.start.getTime(), range.end.getTime(), includeShared, includeTasks, taskEvents, roomId, holidayEvents, searchQuery]);

  useEventReminders(filteredEvents, remindersMap);

  const rangeLabel = useMemo(() => formatRangeLabel(viewMode, anchorDate), [viewMode, anchorDate]);

  const handlePrev = () => {
    const next = new Date(anchorDate);
    switch (viewMode) {
      case 'month': next.setMonth(next.getMonth() - 1); break;
      case 'week':  next.setDate(next.getDate() - 7); break;
      default:      next.setDate(next.getDate() - 1); break;
    }
    setAnchorDate(next);
  };
  const handleNext = () => {
    const next = new Date(anchorDate);
    switch (viewMode) {
      case 'month': next.setMonth(next.getMonth() + 1); break;
      case 'week':  next.setDate(next.getDate() + 7); break;
      default:      next.setDate(next.getDate() + 1); break;
    }
    setAnchorDate(next);
  };
  const handleToday = () => setAnchorDate(new Date());

  const handleEventClick = (ev: CalendarEvent, e?: React.MouseEvent) => {
    // Task 마감일 가상 일정 → 경량 미리보기 dialog (TaskPreviewDialog)
    if (ev.metadata?.__task) {
      setTaskPreview(ev);
      return;
    }
    // 클릭한 마우스 좌표를 0-size 가상 앵커로 → 팝오버가 마우스 바로 아래에 표시
    if (e) {
      const x = e.clientX, y = e.clientY;
      detailAnchorRef.current = { getBoundingClientRect: () => new DOMRect(x, y, 0, 0) } as unknown as HTMLElement;
    }
    const originalId = ev.id.split('::')[0];
    const original = events.find((ev2) => ev2.id === originalId);
    setSelectedEvent(original ?? ev);
  };
  const handleCellClick = (date: Date) => {
    setCreateInitialDate(date); setShowCreate(true);
  };

  // EventCreateModal/EventDetailOverlay 가 useCalendarContext().refreshEvents 의존 → 로컬 Context 주입
  const contextValue = {
    viewMode, setViewMode,
    range, setRange: () => {},
    anchorDate, setAnchorDate,
    events, setEvents,
    taskEvents,
    remindersMap,
    selectedEvent, setSelectedEvent,
    filters: {
      workspace: true, personal: true,
      channels: {},
      showSharedEvents: true, showChannelTasks: true,
      holidays: true, holidayCountries: null,
    },
    setFilters: () => {},
    loading, setLoading,
    refreshEvents,
    attendingEventIds,
    error,
  };

  return (
    <CalendarContext.Provider value={contextValue}>
      <div className="flex-1 flex flex-col h-full min-h-0 bg-background relative">
        {/* ── Header ── 모바일(<sm)은 2행(타이틀 / 네비+버튼), sm 이상은 기존 단일 행 — 한 줄 강제 시 우측 버튼·그리드가 잘림 */}
        <header className="flex flex-col gap-y-1.5 px-3 py-2 shrink-0 border-b border-border sm:flex-row sm:items-center sm:gap-2.5 sm:px-4 sm:py-0 sm:h-14">
          <div className="flex items-center gap-2.5 min-w-0">
            <CalendarDays className="size-5 text-primary shrink-0" strokeWidth={2} />
            <div className="flex items-center gap-1.5 min-w-0">
              <Hash className="size-4 text-muted-foreground shrink-0" />
              <h2 className="text-body-lg font-semibold text-foreground truncate">
                <Trans>{channelName} calendar</Trans>
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-1 min-w-0 sm:ml-2 sm:flex-1">
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={handlePrev}
              className="size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              aria-label={t`Previous`}
            ><ChevronLeft className="size-4" /></button>
            <button
              onClick={handleToday}
              className="h-8 px-2.5 rounded-lg text-label font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
            ><Trans>Today</Trans></button>
            <button
              onClick={handleNext}
              className="size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              aria-label={t`Next`}
            ><ChevronRight className="size-4" /></button>
          </div>

          <span className="text-label text-muted-foreground tabular-nums shrink-0 ml-1">{rangeLabel}</span>

          <div className="ml-auto flex items-center gap-1.5 shrink-0">
            <Select value={viewMode} onValueChange={(v) => setViewMode(v as CalendarViewMode)}>
              <SelectTrigger className="h-8 w-auto gap-1.5 px-2.5 text-label">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {VIEW_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-1.5">
                        <Icon className="size-3.5" />
                        {labels.viewMode(opt.value)}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Button
              size="sm"
              className="ml-1 shrink-0"
              onClick={() => { setCreateInitialDate(null); setShowCreate(true); }}
              title={t`Create event in #${channelName}`}
            >
              {/* 모바일은 아이콘만 (라벨까지 두면 네비 행이 패널 폭을 넘침) */}
              <PenLine className="size-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline"><Trans>New channel event</Trans></span>
            </Button>
          </div>
          </div>
        </header>

        {/* ── 서브 헤더: 필터링 + 키워드 검색 ── 좁으면 체크박스 그룹이 다음 줄로 (한 줄 강제 시 잘림) */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 min-h-10 shrink-0 border-b border-border sm:px-4 sm:py-0 sm:h-10">
          {/* 키워드 검색 */}
          <div className="relative flex-1 min-w-[140px] max-w-sm">
            <Search className="size-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t`Search title, description, location`}
              className="h-7 pl-7 pr-7 text-label"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 size-5 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                aria-label={t`Clear search`}
              ><X className="size-3.5" /></button>
            )}
          </div>

          <div className="ml-auto flex items-center gap-1 shrink-0">
            {/* Other channels shared events toggle */}
            <label className="flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={includeShared}
                onCheckedChange={(v) => toggleIncludeShared(!!v)}
              />
              <span className="text-label text-muted-foreground flex items-center gap-1">
                <span className="size-2 rounded-full bg-primary/50" />
                <Trans>Other channels</Trans>
              </span>
            </label>

            {/* Task deadlines toggle */}
            <label className="flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={includeTasks}
                onCheckedChange={(v) => toggleIncludeTasks(!!v)}
              />
              <span className="text-label text-muted-foreground flex items-center gap-1">
                <span className="size-2 rounded-full bg-sky-500" />
                <Trans>Task deadlines</Trans>
              </span>
            </label>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-hidden min-h-0">
          {error && (
            <div className="flex items-center justify-center h-full text-caption text-destructive">{error}</div>
          )}
          {!error && viewMode === 'month' && (
            <MonthView
              anchorDate={anchorDate}
              events={filteredEvents}
              onEventClick={handleEventClick}
              onCellClick={handleCellClick}
              getStyle={getStyle}
            />
          )}
          {!error && viewMode === 'agenda' && (
            <AgendaView
              anchorDate={anchorDate}
              events={filteredEvents}
              loading={loading}
              onEventClick={handleEventClick}
              getStyle={getStyle}
            />
          )}
          {!error && viewMode === 'week' && (
            <WeekView
              anchorDate={anchorDate}
              events={filteredEvents}
              onEventClick={handleEventClick}
              onCellClick={handleCellClick}
              getStyle={getStyle}
            />
          )}
          {!error && viewMode === 'day' && (
            <DayView
              anchorDate={anchorDate}
              events={filteredEvents}
              onEventClick={handleEventClick}
              onCellClick={handleCellClick}
              getStyle={getStyle}
            />
          )}
        </div>

        {showCreate && (
          <EventCreateModal
            open={showCreate}
            onOpenChange={setShowCreate}
            initialDate={createInitialDate}
            forceChannel={{ channelId: roomId, channelName }}
          />
        )}
        <EventDetailOverlay
          open={!!selectedEvent}
          onOpenChange={(o) => { if (!o) setSelectedEvent(null); }}
          event={selectedEvent}
          anchorRef={detailAnchorRef}
        />
        <TaskPreviewDialog
          open={!!taskPreview}
          onOpenChange={(o) => { if (!o) setTaskPreview(null); }}
          event={taskPreview}
        />
      </div>
    </CalendarContext.Provider>
  );
};

export default CalendarInlineView;
